import React from 'react';
import type { ReactNode, CSSProperties } from 'react';

// A small inline SVG icon set (24-unit grid, 2px stroke, round joins — the
// Lucide convention), plus the bridge that lets the rest of the app keep
// writing "🖨️ Print": `iconify()` swaps a leading emoji for the matching icon
// and drops any emoji it does not know. Labels therefore look the same on
// every device, and no module had to be edited to get there.

export type IconName = keyof typeof PATHS;

// Each entry is the SVG inner markup for that icon.
const PATHS = {
  home: '<path d="M3 11 12 3l9 8"/><path d="M5 10v10h14V10"/><path d="M10 20v-6h4v6"/>',
  users: '<circle cx="9" cy="8" r="3.5"/><path d="M2.5 20a6.5 6.5 0 0 1 13 0"/><path d="M16 4.5a3.5 3.5 0 0 1 0 7"/><path d="M17.5 13.5a6 6 0 0 1 4 6.5"/>',
  user: '<circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/>',
  cake: '<path d="M4 21h16"/><path d="M5 21v-6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v6"/><path d="M5 16c1.5 1.2 3 1.2 4.5 0s3-1.2 4.5 0 3 1.2 4.5 0"/><path d="M8 13V9M12 13V9M16 13V9"/><path d="M8 6a1 1 0 1 0 0-.1M12 6a1 1 0 1 0 0-.1M16 6a1 1 0 1 0 0-.1"/>',
  calendar: '<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18"/><path d="M8 3v4M16 3v4"/>',
  church: '<path d="M12 2v5"/><path d="M9.5 4.5h5"/><path d="m6 21 6-8 6 8"/><path d="M4 21h16"/><path d="M6 21v-7l6-4 6 4v7"/><path d="M10 21v-4h4v4"/>',
  coins: '<circle cx="9" cy="9" r="6"/><path d="M15.5 8.2a6 6 0 1 1-7.3 7.3"/><path d="M7 9h4M9 7v4"/>',
  receipt: '<path d="M5 3h14v18l-2-1.5L15 21l-2-1.5L11 21l-2-1.5L7 21l-2-1.5Z"/><path d="M8 8h8M8 12h8M8 16h5"/>',
  banknote: '<rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="3"/><path d="M6 12h.01M18 12h.01"/>',
  chart: '<path d="M4 20V10"/><path d="M10 20V4"/><path d="M16 20v-8"/><path d="M22 20H2"/>',
  trendUp: '<path d="m3 17 6-6 4 4 8-8"/><path d="M15 7h6v6"/>',
  trendDown: '<path d="m3 7 6 6 4-4 8 8"/><path d="M15 17h6v-6"/>',
  graduation: '<path d="m2 9 10-5 10 5-10 5Z"/><path d="M6 11.5V16c0 1.5 3 3 6 3s6-1.5 6-3v-4.5"/><path d="M22 9v6"/>',
  handHeart: '<path d="M11 14h2a2 2 0 1 0 0-4h-3l-2.5-2.5a3 3 0 0 0-4.2 0L2 9"/><path d="M2 15h4l4 3h6l6-4a2 2 0 0 0-2.5-3L15 13"/><path d="M14 4.5a2.2 2.2 0 0 1 4 0c0 2-4 4-4 4s-4-2-4-4a2.2 2.2 0 0 1 4 0Z"/>',
  cross: '<path d="M12 3v18"/><path d="M6 8h12"/><path d="M8 21h8"/>',
  droplet: '<path d="M12 3s6 6.5 6 11a6 6 0 0 1-12 0c0-4.5 6-11 6-11Z"/>',
  rings: '<circle cx="9" cy="13" r="5"/><circle cx="15" cy="13" r="5"/><path d="M9 8 10.5 5h3L15 8"/>',
  globe: '<circle cx="12" cy="12" r="9"/><path d="M3 12h18"/><path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18Z"/>',
  music: '<path d="M9 18V6l11-2v12"/><circle cx="6" cy="18" r="3"/><circle cx="17" cy="16" r="3"/>',
  flame: '<path d="M12 3s5 4 5 10a5 5 0 0 1-10 0c0-2 1-3.5 2-4.5 0 2 1 3 2 3 0-3 1-6.5 1-8.5Z"/>',
  archive: '<rect x="3" y="4" width="18" height="5" rx="1"/><path d="M5 9v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9"/><path d="M10 13h4"/>',
  clipboard: '<rect x="6" y="4" width="12" height="17" rx="2"/><path d="M9 4a2 2 0 0 1 6 0"/><path d="M9 11h6M9 15h6"/>',
  building: '<rect x="4" y="3" width="16" height="18" rx="1"/><path d="M9 7h2M13 7h2M9 11h2M13 11h2M9 15h2M13 15h2"/><path d="M10 21v-3h4v3"/>',
  settings: '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z"/>',
  moon: '<path d="M20 14.5A8 8 0 0 1 9.5 4 8 8 0 1 0 20 14.5Z"/>',
  sun: '<circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/>',
  logout: '<path d="M10 17l5-5-5-5"/><path d="M15 12H3"/><path d="M13 3h6a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-6"/>',
  printer: '<path d="M6 9V3h12v6"/><rect x="3" y="9" width="18" height="8" rx="2"/><path d="M6 14h12v7H6Z"/>',
  download: '<path d="M12 3v12"/><path d="m7 10 5 5 5-5"/><path d="M4 20h16"/>',
  upload: '<path d="M12 15V3"/><path d="m7 8 5-5 5 5"/><path d="M4 20h16"/>',
  inbox: '<path d="M3 13h5l2 3h4l2-3h5"/><path d="M5 4h14l2 9v7H3v-7Z"/>',
  pencil: '<path d="M4 20h4l11-11-4-4L4 16Z"/><path d="m13 7 4 4"/>',
  trash: '<path d="M4 7h16"/><path d="M9 7V4h6v3"/><path d="M6 7l1 14h10l1-14"/><path d="M10 11v6M14 11v6"/>',
  search: '<circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/>',
  check: '<path d="m4 12 5 5L20 6"/>',
  x: '<path d="M6 6l12 12M18 6 6 18"/>',
  alert: '<path d="M12 3 2 20h20Z"/><path d="M12 9v5"/><path d="M12 17h.01"/>',
  info: '<circle cx="12" cy="12" r="9"/><path d="M12 11v5"/><path d="M12 8h.01"/>',
  map: '<path d="m3 6 6-2 6 2 6-2v14l-6 2-6-2-6 2Z"/><path d="M9 4v14M15 6v14"/>',
  image: '<rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="8.5" cy="9.5" r="1.5"/><path d="m21 16-5-5-8 8"/>',
  eye: '<path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Z"/><circle cx="12" cy="12" r="3"/>',
  activity: '<path d="M3 12h4l3-8 4 16 3-8h4"/>',
  refresh: '<path d="M20 12a8 8 0 1 1-2.3-5.7"/><path d="M20 4v5h-5"/>',
  wrench: '<path d="M14.5 6.5a4 4 0 0 0 5 5L9 22l-3-3L16.5 8.5a4 4 0 0 0-2-2Z"/>',
  file: '<path d="M6 3h8l5 5v13H6Z"/><path d="M14 3v5h5"/><path d="M9 13h6M9 17h6"/>',
  key: '<circle cx="8" cy="15" r="4"/><path d="m11 12 9-9"/><path d="M17 6l2 2M14 9l2 2"/>',
  gift: '<rect x="3" y="8" width="18" height="4"/><path d="M5 12v9h14v-9"/><path d="M12 8v13"/><path d="M12 8c-2-4-6-3-6-1s3 1 6 1c3 0 6 1 6-1s-4-3-6 1Z"/>',
  hourglass: '<path d="M6 2h12"/><path d="M6 22h12"/><path d="M7 2v5l5 5-5 5v5"/><path d="M17 2v5l-5 5 5 5v5"/>',
  clock: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>',
  phone: '<path d="M5 3h4l2 5-2.5 1.5a11 11 0 0 0 6 6L16 13l5 2v4a2 2 0 0 1-2 2A17 17 0 0 1 3 5a2 2 0 0 1 2-2Z"/>',
  mail: '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/>',
  medical: '<rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 8v8M8 12h8"/>',
  chat: '<path d="M21 12a8 8 0 0 1-11.5 7.2L4 21l1.8-4.5A8 8 0 1 1 21 12Z"/>',
  book: '<path d="M4 4h6a3 3 0 0 1 3 3v13a2 2 0 0 0-2-2H4Z"/><path d="M20 4h-6a3 3 0 0 0-3 3v13a2 2 0 0 1 2-2h7Z"/>',
  zap: '<path d="M13 2 4 14h7l-1 8 9-12h-7Z"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
  briefcase: '<rect x="3" y="7" width="18" height="13" rx="2"/><path d="M9 7V4h6v3"/><path d="M3 12h18"/>',
  package: '<path d="m3 7 9-4 9 4v10l-9 4-9-4Z"/><path d="M3 7l9 4 9-4"/><path d="M12 11v10"/>',
  landmark: '<path d="M3 21h18"/><path d="M5 21V10M9 21V10M15 21V10M19 21V10"/><path d="m2 10 10-6 10 6Z"/>',
  star: '<path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1 6.2L12 17.3 6.5 20.2l1-6.2L3 9.6l6.2-.9Z"/>',
  dove: '<path d="M21 6c-3 0-6 2-8 5l-3-3-7 1 4 3-1 6 5-3 2 3c4-1 8-5 8-12Z"/><path d="M13 11h.01"/>',
  handshake: '<path d="m11 17 2 2a1.5 1.5 0 0 0 2-2"/><path d="m14 14 2.5 2.5a1.5 1.5 0 0 0 2-2L13 9a3 3 0 0 0-4 0l-1 1-3-3L2 10l4 4"/><path d="m11 8 2-2 5 2 2-1"/>',
  bread: '<path d="M4 10a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v9H4Z"/><path d="M8 6c0 3 2 4 4 4s4-1 4-4"/>',
  history: '<path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/><path d="M12 7v5l3 2"/>',
  save: '<path d="M5 3h11l3 3v15H5Z"/><path d="M8 3v5h7V3"/><path d="M8 21v-6h8v6"/>',
  menu: '<path d="M4 6h16M4 12h16M4 18h16"/>',
  arrowLeft: '<path d="M19 12H5"/><path d="m12 19-7-7 7-7"/>',
  arrowRight: '<path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>',
  chevronUp: '<path d="m6 15 6-6 6 6"/>',
  chevronDown: '<path d="m6 9 6 6 6-6"/>',
  pin: '<path d="M12 21s-6-5.5-6-11a6 6 0 0 1 12 0c0 5.5-6 11-6 11Z"/><circle cx="12" cy="10" r="2"/>',
  ban: '<circle cx="12" cy="12" r="9"/><path d="m5.5 5.5 13 13"/>',
  layers: '<path d="m12 3 9 5-9 5-9-5Z"/><path d="m3 12 9 5 9-5"/><path d="m3 16 9 5 9-5"/>',
  trophy: '<path d="M8 4h8v5a4 4 0 0 1-8 0Z"/><path d="M8 6H5v2a3 3 0 0 0 3 3"/><path d="M16 6h3v2a3 3 0 0 1-3 3"/><path d="M12 13v4"/><path d="M8 21h8M9 17h6v4"/>',
  card: '<rect x="3" y="6" width="18" height="12" rx="2"/><path d="M3 10h18"/><path d="M7 15h4"/>',
  scale: '<path d="M12 3v18"/><path d="M5 7h14"/><path d="m5 7-3 7a3 3 0 0 0 6 0Z"/><path d="m19 7-3 7a3 3 0 0 0 6 0Z"/><path d="M8 21h8"/>',
  palm: '<path d="M12 22v-9"/><path d="M12 13c-4 0-7-3-7-7 3 0 5 1 7 3 2-2 4-3 7-3 0 4-3 7-7 7Z"/><path d="M12 13c0-3-2-6-4-8M12 13c0-3 2-6 4-8"/>',
  crown: '<path d="m3 8 4 4 5-7 5 7 4-4-2 11H5Z"/>',
  walk: '<circle cx="13" cy="4" r="1.5"/><path d="m8 21 3-7-2-5 4 1 2 3 3 1"/><path d="m11 14 3 3-1 4"/>',
  lock: '<rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/>',
  mic: '<rect x="9" y="3" width="6" height="11" rx="3"/><path d="M6 11a6 6 0 0 0 12 0"/><path d="M12 17v4M9 21h6"/>',
  party: '<path d="M4 20 8 6l10 10Z"/><path d="M14 4v2M18 8h2M17 4l-1 1"/>',
  luggage: '<rect x="5" y="7" width="14" height="13" rx="2"/><path d="M9 7V4h6v3"/><path d="M9 20v1M15 20v1"/>',
  hand: '<path d="M8 12V6a1.5 1.5 0 0 1 3 0v5"/><path d="M11 11V4a1.5 1.5 0 0 1 3 0v7"/><path d="M14 11V6a1.5 1.5 0 0 1 3 0v6"/><path d="M17 12V9a1.5 1.5 0 0 1 3 0v6a6 6 0 0 1-6 6h-2a6 6 0 0 1-5-2.5L4 13a1.5 1.5 0 0 1 2.5-1.7L8 13"/>',
  bank: '<path d="M3 21h18"/><path d="M4 9h16v10H4Z"/><path d="m2 9 10-6 10 6"/><path d="M8 13v4M12 13v4M16 13v4"/>',
  eyeOff: '<path d="M3 3l18 18"/><path d="M10.5 6.2A10 10 0 0 1 12 6c6 0 10 6 10 6a17 17 0 0 1-3.4 3.8"/><path d="M6.6 6.6A17 17 0 0 0 2 12s4 6 10 6a10 10 0 0 0 3.4-.6"/><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2"/>',
  reply: '<path d="m9 14-5-5 5-5"/><path d="M4 9h11a5 5 0 0 1 5 5v5"/>',
  skipForward: '<path d="m5 4 10 8-10 8Z"/><path d="M19 4v16"/>',
  circle: '<circle cx="12" cy="12" r="8"/>'
} as const;

export interface IconProps {
  name: IconName;
  size?: number;
  strokeWidth?: number;
  style?: CSSProperties;
  className?: string;
  /** Screen-reader label; omit when the icon only decorates a text label. */
  title?: string;
}

const Icon = ({ name, size = 16, strokeWidth = 2, style, className, title }: IconProps) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden={title ? undefined : true}
    role={title ? 'img' : undefined}
    style={{ flexShrink: 0, verticalAlign: '-0.15em', ...style }}
    dangerouslySetInnerHTML={{ __html: (title ? `<title>${title}</title>` : '') + PATHS[name] }}
  />
);

export default Icon;

// --- Emoji bridge -------------------------------------------------------------

/** Emoji the app's labels start with, and the icon each stands for. */
export const EMOJI_ICON: Record<string, IconName> = {
  '🏠': 'home', '👥': 'users', '👨‍👩‍👧‍👦': 'users', '👨‍👩‍👧': 'users', '👤': 'user', '🎂': 'cake',
  '📅': 'calendar', '🗓️': 'calendar', '📆': 'calendar', '⛪': 'church', '💒': 'church',
  '💰': 'coins', '🪙': 'coins', '🧾': 'receipt', '💸': 'banknote', '💳': 'card', '🏦': 'bank',
  '📊': 'chart', '📈': 'trendUp', '📉': 'trendDown', '🏫': 'graduation', '🎓': 'graduation',
  '🤲': 'handHeart', '🙏': 'hand', '🪦': 'cross', '⚰️': 'cross', '💧': 'droplet', '💍': 'rings',
  '🌍': 'globe', '🎵': 'music', '🎶': 'music', '🔥': 'flame', '🗄️': 'archive', '📋': 'clipboard',
  '📑': 'clipboard', '📜': 'file', '📄': 'file', '📝': 'pencil', '✍️': 'pencil', '🏢': 'building',
  '⚙️': 'settings', '🌙': 'moon', '☀️': 'sun', '🚪': 'logout', '🖨️': 'printer', '⬇️': 'download',
  '⬇': 'download', '⬆️': 'upload', '📤': 'upload', '📥': 'inbox', '📨': 'inbox', '✏️': 'pencil',
  '✎': 'pencil', '🗑️': 'trash', '🔍': 'search', '✅': 'check', '✔️': 'check', '✔': 'check',
  '☑': 'check', '✕': 'x', '❌': 'x', '🚫': 'ban', '⚠️': 'alert', '⚠': 'alert', 'ℹ️': 'info',
  '🗺️': 'map', '🖼️': 'image', '👁️': 'eye', '🙈': 'eyeOff', '🩺': 'activity', '🔄': 'refresh',
  '↻': 'refresh', '↺': 'refresh', '♻️': 'refresh', '🧰': 'wrench', '🛠️': 'wrench', '🔑': 'key',
  '🔐': 'lock', '🎁': 'gift', '⏳': 'hourglass', '⌛': 'hourglass', '🕐': 'clock', '🕒': 'clock',
  '📞': 'phone', '✉️': 'mail', '🏥': 'medical', '🛋️': 'chat', '📖': 'book', '📚': 'book',
  '⚡': 'zap', '➕': 'plus', '💼': 'briefcase', '📦': 'package', '🏛️': 'landmark', '⭐': 'star',
  '🕊️': 'dove', '🤝': 'handshake', '🍞': 'bread', '💾': 'save', '☰': 'menu', '←': 'arrowLeft',
  '→': 'arrowRight', '↩️': 'reply', '⏭️': 'skipForward', '▲': 'chevronUp', '▴': 'chevronUp',
  '▼': 'chevronDown', '▾': 'chevronDown', '📍': 'pin', '🏆': 'trophy', '⚖️': 'scale',
  '🌴': 'palm', '👑': 'crown', '🚶': 'walk', '🎤': 'mic', '🎉': 'party', '🧳': 'luggage',
  '○': 'circle', '☐': 'circle'
};

// A leading emoji (with optional variation selector and ZWJ sequences), or one
// of the plain symbols the app uses as icons.
const LEADING = /^((?:[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}\u{2190}-\u{21FF}\u{231A}-\u{23FF}\u{25A0}-\u{25FF}\u{2700}-\u{27BF}]\u{FE0F}?(?:\u{200D}[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]\u{FE0F}?)*)|[☐☑○])\s*/u;

/**
 * "🖨️ Print / Save PDF" -> [<Icon printer/>, "Print / Save PDF"]. Text with no
 * leading emoji, or a non-string label, is returned untouched. An emoji the
 * set does not know is removed rather than shown, so no label mixes the two.
 */
export const iconify = (label: ReactNode, size = 15): ReactNode => {
  if (typeof label !== 'string') return label;
  const m = LEADING.exec(label);
  if (!m) return label;
  const name = EMOJI_ICON[m[1]] || EMOJI_ICON[m[1].replace(/️/g, '')];
  const rest = label.slice(m[0].length);
  if (!name) return rest || label;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      <Icon name={name} size={size} />
      {rest && <span>{rest}</span>}
    </span>
  );
};

/** The icon for a module's emoji (from the MODULES registry), or a fallback. */
export const iconFor = (emoji: ReactNode, fallback: IconName = 'layers'): IconName => {
  if (typeof emoji !== 'string') return fallback;
  const key = emoji.trim();
  return EMOJI_ICON[key] || EMOJI_ICON[key.replace(/️/g, '')] || fallback;
};
